import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiConsumer } from '../../service/api-consumer';
import { CashRegisterDataRequest } from '../../interface/request/CashRegisterRequest';
import { CashRegisterDataResponse, CashRegisterItem } from '../../interface/response/CashRegisterDataResponse';

/**
 * Async thunk for fetching cash register data.
 *
 * This thunk:
 * - Accepts a CashRegisterDataRequest payload
 * - Calls ApiConsumer.getCashRegisterData
 * - Returns the API response which becomes the 'fulfilled' action payload
 *
 * @async
 * @function fetchCashRegisterData
 * @param {CashRegisterDataRequest} payload - The request parameters sent to the API.
 * @returns {Promise<CashRegisterDataResponse>} Resolves with API response.
 *
 * @example
 * dispatch(fetchCashRegisterData({ dateFrom, dateTo, locationId }));
 *
 * Shape of the Redux state for the cash register module.
 *
 * @interface DataState
 * @property {CashRegisterDataResponse | null} data - The last loaded cash register data.
 * @property {boolean} loading - Indicates whether the request is currently in progress.
 * @property {unknown | null} error - Contains an error object if the last request failed.
 * @property {string} selectedLocation - Currently selected location for filtering or display.
 * @property {string} selectedCCY - Currently selected currency code (e.g., 'USD', 'EUR').
 * @property {CashRegisterItem | null} selectedItem - Currently selected cash register item.
 *
 * Redux slice responsible for:
 * - Fetching cash register data
 * - Storing loading/error/data state
 * - Managing selected location, currency, and item
 * - Resetting cash register state
 *
 * Contains:
 * - Reducers for resetting cash register data and updating selected values
 * - Extra reducers for handling thunk lifecycle (pending/fulfilled/rejected)
 */

export const fetchCashRegisterData = createAsyncThunk(
  'data/fetchCashRegisterData',
  async (payload: CashRegisterDataRequest) => {
    const response = await ApiConsumer.getCashRegisterData(payload);
    return response;
  },
);
interface DataState {
  data: CashRegisterDataResponse | null;
  loading: boolean;
  error: null | unknown;
  selectedLocation: string;
  selectedCCY: string;
  selectedItem: CashRegisterItem | null;
}

const initialState: DataState = {
  data: null,
  loading: false,
  error: null,
  selectedCCY: '',
  selectedLocation: '',
  selectedItem: null,
};

const cashRegisterSlice = createSlice({
  name: 'cashRegisterData',
  initialState,
  reducers: {
    resetCashRegister: (state) => {
      state.loading = false;
      state.error = null;
      state.data = null;
    },
    setSelectedLocation: (state, action) => {
      state.selectedLocation = action.payload;
    },
    setSelectedCCY: (state, action) => {
      state.selectedCCY = action.payload;
    },
    setSelectedItem: (state, action) => {
      state.selectedItem = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCashRegisterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCashRegisterData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchCashRegisterData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  },
});

export const { resetCashRegister, setSelectedCCY, setSelectedLocation, setSelectedItem } = cashRegisterSlice.actions;
export default cashRegisterSlice.reducer;
